import { Loader2 } from "lucide-react";
import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "./Dialog";

function focusLeftTheControls(dialog: HTMLDialogElement | null): boolean {
  const active = document.activeElement;
  return active === null || active === document.body || active === dialog;
}

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  pendingLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  isPending?: boolean;
  closeOnConfirm?: boolean;
  hideCancel?: boolean;
  error?: string;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  pendingLabel,
  cancelLabel = "Cancel",
  destructive = false,
  isPending = false,
  closeOnConfirm = true,
  hideCancel = false,
  error,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedPendingLabel = pendingLabel ?? t("common.pleaseWait");
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const focusWasPlacedOnConfirm = useRef(false);
  const wasOpen = useRef(false);

  useLayoutEffect(() => {
    const confirm = confirmRef.current;
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (!open || !confirm) {
      focusWasPlacedOnConfirm.current = false;
      return;
    }
    if (hideCancel) {
      if (focusLeftTheControls(confirm.closest("dialog"))) {
        confirm.focus();
        focusWasPlacedOnConfirm.current = true;
      } else if (opening && document.activeElement === confirm) {
        focusWasPlacedOnConfirm.current = true;
      }
      return;
    }
    if (focusWasPlacedOnConfirm.current && document.activeElement === confirm) {
      cancelRef.current?.focus();
    }
    focusWasPlacedOnConfirm.current = false;
  }, [open, hideCancel]);

  const handleConfirm = () => {
    if (isPending) return;
    onConfirm();
    if (closeOnConfirm) {
      onClose();
    }
  };

  const handleCancel = () => {
    if (isPending) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} title={title} description={description} error={error}>
      <div className="flex justify-end gap-3 pt-2">
        {hideCancel ? null : (
          <button
            ref={cancelRef}
            type="button"
            onClick={handleCancel}
            aria-disabled={isPending || undefined}
            data-autofocus={!isPending || undefined}
            className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:hover:border-border aria-disabled:hover:text-muted"
          >
            {cancelLabel}
          </button>
        )}
        <button
          ref={confirmRef}
          type="button"
          onClick={handleConfirm}
          aria-disabled={isPending || undefined}
          aria-busy={isPending || undefined}
          data-autofocus={(hideCancel && !isPending) || undefined}
          className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all aria-disabled:opacity-50 aria-disabled:cursor-not-allowed ${
            destructive
              ? "bg-danger hover:bg-danger/90 aria-disabled:hover:bg-danger"
              : "bg-indigo hover:bg-indigo-bright aria-disabled:hover:bg-indigo"
          }`}
        >
          {isPending && <Loader2 size={16} className="animate-spin" />}
          {isPending ? resolvedPendingLabel : confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}
